import DESIGN_PACKS_CATALOG from '@/generated/design-packs-catalog.json';
import DESIGN_PACKS_EDU_CATALOG from '@/generated/design-packs-edu-catalog.json';

const LOOK_SHELF = [...DESIGN_PACKS_CATALOG, ...DESIGN_PACKS_EDU_CATALOG] as unknown as { id: string; shelf?: { clipTheme?: string } }[];

export function visibleDesignPacks<T extends { requiresFeature?: string }>(
  packs: readonly T[],
  grantedFeatures: readonly string[],
): T[] {
  return packs.filter((p) => !p.requiresFeature || grantedFeatures.includes(p.requiresFeature));
}

export interface PackVariant {
  packId: string;
  name: string;
  kind: 'theme' | 'preset';
}

/** A style card of the generated catalog: the base, or (when the base does not serve this picker) its first variant. */
export interface StyleCardLike {
  id: string;
  name: string;
  packId?: string;
  variants?: PackVariant[];
  aliases?: string[];
}

export const cardPackId = (card: StyleCardLike): string => card.packId ?? card.id;

/** The theme a style's card shows, so that picking the card builds in it. Only a style's own id has one: an old id or a variant carries its own theme. */
export function cardTheme(cards: readonly { id: string; shelf?: { clipTheme?: string } }[], packId: string): string | undefined {
  return cards.find((c) => c.id === packId)?.shelf?.clipTheme;
}

export function cardHolds(card: StyleCardLike, packId: string | null | undefined): boolean {
  if (!packId) return false;
  return packId === card.id || packId === card.packId || !!card.variants?.some((v) => v.packId === packId);
}

/** The picker name of a pack id: a style, or one of its variants ("Studio Object · Macro reel"). */
export function packLabel(cards: readonly StyleCardLike[], packId: string): string | undefined {
  const card = cards.find((c) => cardHolds(c, packId));
  if (!card) return undefined;
  const variant = card.variants?.find((v) => v.packId === packId);
  return variant ? `${card.name} · ${variant.name}` : card.name;
}

/** A look chosen from the gallery, for the storyboard: with its theme and still when it's a style in the theme that still shows. */
export function lookChosenFrom(ref: string, name: string): { ref: string; name: string; theme?: string; poster?: string } {
  const theme = cardTheme(LOOK_SHELF, ref);
  return theme ? { ref, name, theme, poster: `${window.location.origin}/design-packs/looks/${ref}.jpg` } : { ref, name };
}

